import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import DateRangeIcon from '@mui/icons-material/DateRange';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import type { ChangeEvent, CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useSecurityDashboardGrid } from './useSecurityDashboardGrid';
import { SecurityClientFilter } from './SecurityClientFilter';
import { useEdrGlobalFiltersAccess } from 'src/mindtime/hooks/useEdrGlobalFiltersAccess';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';

const DATE_RANGE_FILTER_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

export const isSecurityFilterApplied = (value?: string | null): boolean => Boolean(value);

/** Split comma-separated toolbar filter values (multi-select). */
export const splitSecurityFilterValues = (value: string): string[] =>
  value.split(',').map((part) => part.trim()).filter(Boolean);

export const getSecurityToolbarFilterStyles = (
  theme: Theme,
  applied: boolean,
  boxOverrides?: CSSProperties
) => {
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;

  return {
    box: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      height: 36,
      padding: '0 12px',
      border: `1px solid ${applied ? accent : theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: applied ? alpha(accent, 0.12) : theme.palette.background.paper,
      boxSizing: 'border-box' as const,
      width: '100%',
      minWidth: 0,
      ...boxOverrides,
    },
    text: {
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      lineHeight: '20px',
      color: applied ? accent : muted,
      whiteSpace: 'nowrap' as const,
    },
    iconColor: applied ? accent : muted,
  };
};

export const COMPACT_TOOLBAR_FILTER_OVERRIDES: CSSProperties = {
  height: 34,
  padding: '0 10px',
  gap: 6,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  width: 'auto',
  justifyContent: 'space-between',
};

type SecurityToolbarFilterPillProps = {
  applied: boolean;
  label: ReactNode;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  leadingIcon?: ReactNode;
  boxStyleOverrides?: CSSProperties;
  textStyleOverrides?: CSSProperties;
  iconSize?: number;
};

export const SecurityToolbarFilterPill: FC<SecurityToolbarFilterPillProps> = ({
  applied,
  label,
  onClick,
  leadingIcon,
  boxStyleOverrides,
  textStyleOverrides,
  iconSize = 18,
}) => {
  const theme = useTheme();
  const { box, text, iconColor } = getSecurityToolbarFilterStyles(theme, applied, {
    cursor: 'pointer',
    justifyContent: 'space-between',
    ...boxStyleOverrides,
  });

  return (
    <Box role="button" tabIndex={0} onClick={onClick} style={box}>
      <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0, flex: 1 }}>
        {leadingIcon ? (
          <Box
            component="span"
            sx={{ display: 'flex', color: iconColor, '& svg': { fontSize: iconSize } }}
          >
            {leadingIcon}
          </Box>
        ) : null}
        <Box
          component="span"
          style={{
            ...text,
            ...textStyleOverrides,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Box>
      </Box>
      <KeyboardArrowDownIcon style={{ fontSize: iconSize, color: iconColor, flexShrink: 0 }} />
    </Box>
  );
};

const ToolbarSearchInput = styled.input({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  lineHeight: '20px',
  padding: 0,
  '&::placeholder': {
    opacity: 0.75,
  },
});

type SecurityDashboardToolbarSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export const SecurityToolbarSearch: FC<SecurityDashboardToolbarSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search devices, agents or commands...',
}) => {
  const theme = useTheme();
  const applied = Boolean(value.trim());
  const { box, iconColor } = getSecurityToolbarFilterStyles(theme, applied);

  return (
    <Box style={box}>
      <SearchIcon style={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
      <ToolbarSearchInput
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        placeholder={placeholder}
        style={{ color: iconColor }}
      />
    </Box>
  );
};

const SecurityDashboardToolbarSearch: FC<SecurityDashboardToolbarSearchProps> = (props) => (
  <SecurityToolbarSearch {...props} />
);

export type SecurityToolbarFilterOption = {
  label: string;
  value: string;
};

export type SecurityToolbarFilterProps = {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  minWidth?: number;
  compact?: boolean;
  options?: SecurityToolbarFilterOption[];
  value?: string;
  onChange?: (value: string) => void;
  /** Comma-separated `value`; menu stays open while toggling. */
  multiple?: boolean;
  /** When false, omit the synthetic “All {label}” clear option (e.g. global Date Range). */
  showAllOption?: boolean;
};

export const SecurityToolbarFilter: FC<SecurityToolbarFilterProps> = ({
  label,
  icon,
  onClick,
  minWidth,
  compact = false,
  options,
  value = '',
  onChange,
  multiple = false,
  showAllOption = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasDropdown = Boolean(Array.isArray(options) && onChange);
  const selectedValues = multiple ? splitSecurityFilterValues(value) : value ? [value] : [];
  const applied = selectedValues.length > 0;
  const hasBuiltInAllOption = options?.some((option) => option.value === '');
  const isEmpty = hasDropdown && (options?.length ?? 0) === 0;
  const shouldShowAllOption = showAllOption && !hasBuiltInAllOption;

  let displayLabel = label;
  if (hasDropdown && selectedValues.length === 1) {
    displayLabel = options?.find((option) => option.value === selectedValues[0])?.label ?? label;
  } else if (hasDropdown && selectedValues.length > 1) {
    displayLabel = `${label} (${selectedValues.length})`;
  }

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (hasDropdown) {
      setAnchorEl(event.currentTarget);
      return;
    }
    onClick?.();
  };

  const handleSelect = (nextValue: string) => {
    if (!multiple) {
      onChange?.(nextValue);
      setAnchorEl(null);
      return;
    }

    if (!nextValue) {
      onChange?.('');
      setAnchorEl(null);
      return;
    }

    const nextValues = selectedValues.includes(nextValue)
      ? selectedValues.filter((item) => item !== nextValue)
      : [...selectedValues, nextValue];
    onChange?.(nextValues.join(','));
  };

  return (
    <>
      <SecurityToolbarFilterPill
        applied={applied}
        label={displayLabel}
        onClick={handleOpen}
        leadingIcon={icon}
        iconSize={compact ? 16 : 18}
        textStyleOverrides={compact ? { fontSize: 12, lineHeight: '18px' } : undefined}
        boxStyleOverrides={{
          ...(compact ? COMPACT_TOOLBAR_FILTER_OVERRIDES : {}),
          width: minWidth || 'auto',
          minWidth: minWidth ?? 0,
          flex: minWidth ? `0 0 ${minWidth}px` : '0 0 auto',
        }}
      />
      {hasDropdown ? (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 160 } }}
        >
          {shouldShowAllOption ? (
            <MenuItem
              selected={selectedValues.length === 0}
              onClick={() => handleSelect('')}
              style={{ fontSize: 13 }}
            >
              {`All ${label}`}
            </MenuItem>
          ) : null}
          {isEmpty ? (
            <Box style={{ padding: '8px 16px', fontSize: 12, color: '#94A3B8' }}>
              No item found
            </Box>
          ) : (
            options?.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <MenuItem
                  key={option.value}
                  selected={!multiple && isSelected}
                  onClick={() => handleSelect(option.value)}
                  style={{ fontSize: 13 }}
                >
                  {multiple ? (
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      tabIndex={-1}
                      onClick={(event) => event.preventDefault()}
                      style={{ padding: 0, marginRight: 8 }}
                    />
                  ) : null}
                  {option.label}
                </MenuItem>
              );
            })
          )}
        </Menu>
      ) : null}
    </>
  );
};

const SecurityDashboardToolbarDateFilter: FC = () => {
  const { dateRange, setDateRange } = useWazuhScope();
  const { canDateRange } = useEdrGlobalFiltersAccess();

  if (!canDateRange) return null;

  return (
    <SecurityToolbarFilter
      label="Date Range"
      icon={<DateRangeIcon style={{ fontSize: 18 }} />}
      options={DATE_RANGE_FILTER_OPTIONS}
      value={dateRange}
      showAllOption={false}
      onChange={(value) => setDateRange(value as WazuhDateRangeOption)}
    />
  );
};

type SecurityDashboardToolbarProps = {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** When false, the search field is omitted. */
  showSearch?: boolean;
  /** When false, the reseller client filter slot is omitted entirely. */
  showClientFilter?: boolean;
  /** `inline` — fixed-width controls for header row; `full` — stretches to container width */
  layout?: 'inline' | 'full';
};

export const SecurityDashboardToolbar: FC<SecurityDashboardToolbarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  showClientFilter = true,
  layout = 'inline',
}) => {
  const { toolbarStack } = useSecurityDashboardGrid();
  const { isReseller } = useWazuhScope();
  const { canDateRange, canOrganization, canSearch } = useEdrGlobalFiltersAccess();
  const isFullWidth = layout === 'full';
  const shouldShowSearch = showSearch && canSearch && Boolean(onSearchChange);
  const shouldShowClientFilter = showClientFilter && isReseller && canOrganization;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={toolbarStack ? 'column' : 'row'}
      style={{
        gap: 12,
        width: toolbarStack || isFullWidth ? '100%' : 'auto',
        minWidth: 0,
        flex: toolbarStack || isFullWidth ? '1 1 auto' : '0 1 auto',
        justifyContent: isFullWidth && !toolbarStack && !shouldShowClientFilter ? 'flex-start' : undefined,
      }}
    >
      {shouldShowSearch ? (
      <Box
        style={{
          flex:
            isFullWidth && !toolbarStack
              ? shouldShowClientFilter
                ? '1.6 1 0'
                : '1 1 0'
              : undefined,
          width: toolbarStack ? '100%' : isFullWidth ? undefined : 300,
          minWidth: toolbarStack ? undefined : isFullWidth ? 220 : 0,
        }}
      >
        <SecurityDashboardToolbarSearch
          value={searchValue}
          onChange={onSearchChange!}
          placeholder={searchPlaceholder}
        />
      </Box>
      ) : null}
      {shouldShowClientFilter ? (
        <Box
          style={{
            flex: isFullWidth && !toolbarStack ? '1 1 0' : undefined,
            width: toolbarStack ? '100%' : isFullWidth ? undefined : 160,
            minWidth: toolbarStack ? undefined : isFullWidth ? 150 : 0,
          }}
        >
          <SecurityClientFilter />
        </Box>
      ) : null}
      {canDateRange ? (
        <Box
          style={{
            flex: isFullWidth && !toolbarStack && shouldShowClientFilter ? '1 1 0' : undefined,
            width: toolbarStack ? '100%' : isFullWidth ? undefined : 180,
            minWidth: toolbarStack ? undefined : isFullWidth ? 180 : 0,
            flexShrink: isFullWidth && !toolbarStack && !shouldShowClientFilter ? 0 : undefined,
          }}
        >
          <SecurityDashboardToolbarDateFilter />
        </Box>
      ) : null}
    </Box>
  );
};

type SecurityDashboardHeaderProps = {
  showClientFilter?: boolean;
  welcomeLabel?: string;
};

/**
 * Dashboard page chrome: welcome title, then All Clients + Date Range below (left-aligned).
 * Matches EDR list-page vertical rhythm (no Search on dashboards).
 */
export const SecurityDashboardHeader: FC<SecurityDashboardHeaderProps> = ({
  showClientFilter = true,
  welcomeLabel,
}) => {
  const { userDisplayName, isReseller } = useWazuhScope();
  const resolvedWelcome = welcomeLabel ?? `Welcome ${userDisplayName}`;
  const shouldShowClientFilter = showClientFilter && isReseller;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      style={{ gap: 16, width: '100%' }}
    >
      <Box
        component="span"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 22,
          fontWeight: 700,
          lineHeight: '28px',
          color: '#1E293B',
          flexShrink: 0,
        }}
      >
        {resolvedWelcome}
      </Box>
      <SecurityDashboardToolbar
        showSearch={false}
        showClientFilter={shouldShowClientFilter}
        layout="inline"
      />
    </Box>
  );
};
